/**
 * overBodyParse — token-aware parser for a window `OVER (…)` body into the
 * structured `OverBodySpec`. Reuses the shared clause splitters
 * (`splitTopLevelCommas`) and ORDER-BY item parser (`parseOrderItem`) rather
 * than hand-rolling either, plus the resolver `tokenSkip` helpers so
 * dialect-specific quoting / strings / comments are skipped.
 *
 * Round-trip safety net: after structuring, we re-serialize and compare
 * (case- + whitespace-insensitive) against the input. If they don't match, the
 * body is flagged `parseable: false` and its raw bytes are kept — the editor
 * then falls back to the raw textarea rather than silently rewriting the SQL.
 */
import type { ParseOptions } from '../../../../sql-grammar/model';
import { skipContextFor, trySkipNonCode } from '../../../../sql-resolver/classify/tokenSkip';
import type { SkipContext } from '../../../../sql-resolver/classify/tokenSkip';
import type { FrameBound, FrameSpec, FrameUnit, OverBodySpec } from './overBodySpec';
import { isValidFrameOrder } from './overBodySpec';
import { serializeOverBody } from './overBodySerialize';
import { parseOrderItem } from './parseSelectItem';
import { splitTopLevelCommas } from './splitTopLevel';

const IDENT_START = /[A-Za-z_]/;
const IDENT_CONT = /[A-Za-z0-9_$]/;
const FRAME_UNITS: ReadonlySet<string> = new Set(['ROWS', 'RANGE', 'GROUPS']);

interface Word {
  readonly start: number;
  readonly end: number;
  readonly upper: string;
}

export function parseOverBody(overBody: string, options: ParseOptions): OverBodySpec {
  const raw = overBody;
  const trimmed = overBody.trim();
  if (trimmed.length === 0) {
    return { partitionBy: [], orderBy: [], frame: null, frameExclusion: null, parseable: true, raw };
  }

  const ctx = skipContextFor(options);
  const words = scanDepth0Words(overBody, 0, overBody.length, ctx);

  const partitionByIdx = findKeywordPair(words, 'PARTITION', 'BY');
  const orderByIdx = findKeywordPair(words, 'ORDER', 'BY');
  const frameIdx = words.findIndex((w) => FRAME_UNITS.has(w.upper));

  // Segment boundaries (character offsets into `overBody`).
  const partExprStart = partitionByIdx >= 0 ? words[partitionByIdx + 1].end : -1;
  const orderExprStart = orderByIdx >= 0 ? words[orderByIdx + 1].end : -1;
  const frameStart = frameIdx >= 0 ? words[frameIdx].start : -1;

  const partExprEnd = firstOffset([orderStartOffset(words, orderByIdx), frameStart], overBody.length);
  const orderExprEnd = firstOffset([frameStart], overBody.length);

  const partitionBy =
    partitionByIdx >= 0
      ? splitTopLevelCommas(overBody, partExprStart, partExprEnd, options).map((i) => i.text)
      : [];

  const orderBy =
    orderByIdx >= 0
      ? splitTopLevelCommas(overBody, orderExprStart, orderExprEnd, options).map((i) =>
          parseOrderItem(overBody, i.start, i.end, options),
        )
      : [];

  let frame: FrameSpec | null = null;
  let frameExclusion: string | null = null;
  if (frameIdx >= 0) {
    const excludeIdx = words.findIndex((w, k) => k > frameIdx && w.upper === 'EXCLUDE');
    const unitEnd = words[frameIdx].end;
    const frameBodyEnd = excludeIdx >= 0 ? words[excludeIdx].start : overBody.length;
    frame = parseFrame(words[frameIdx].upper, overBody.slice(unitEnd, frameBodyEnd));
    if (excludeIdx >= 0) frameExclusion = overBody.slice(words[excludeIdx].start, overBody.length).trim();
    // A frame keyword we couldn't structure into bounds ⇒ fall back.
    if (frame === null) return unparseable(raw);
    // An ordering-invalid frame (end ranks before start — a pasted
    // `ROWS BETWEEN CURRENT ROW AND 2 PRECEDING`, or a start after the end) parses
    // into bounds cleanly, but MUST NOT mount the structured editor: a guided
    // offset edit there could re-emit it invalid (the unclamped cross-kind
    // `clampFrameOffset` path). Drop it to the raw-text teaching fallback instead
    // — this wires the otherwise production-dead `isValidFrameOrder` to its
    // intended consumer (Item I3). `end === null` means an implicit CURRENT ROW.
    if (!isValidFrameOrder(frame.start, frame.end ?? { kind: 'current-row' })) {
      return unparseable(raw);
    }
  }

  const spec: OverBodySpec = {
    partitionBy,
    orderBy,
    frame,
    frameExclusion,
    parseable: true,
    raw,
  };

  // Self-check: the structured result MUST reproduce the input semantically.
  // Otherwise keep the raw bytes so we never silently rewrite the user's SQL.
  return normalize(serializeOverBody(spec)) === normalize(trimmed) ? spec : unparseable(raw);
}

function unparseable(raw: string): OverBodySpec {
  return { partitionBy: [], orderBy: [], frame: null, frameExclusion: null, parseable: false, raw };
}

function orderStartOffset(words: readonly Word[], orderByIdx: number): number {
  return orderByIdx >= 0 ? words[orderByIdx].start : -1;
}

/** First non-negative offset among candidates, else `fallback`. */
function firstOffset(candidates: readonly number[], fallback: number): number {
  const present = candidates.filter((c) => c >= 0);
  return present.length > 0 ? Math.min(...present) : fallback;
}

function findKeywordPair(words: readonly Word[], first: string, second: string): number {
  for (let i = 0; i < words.length - 1; i++) {
    if (words[i].upper === first && words[i + 1].upper === second) return i;
  }
  return -1;
}

/** Depth-0 identifier words (skips strings / comments / quoted identifiers and
 *  anything inside parentheses). Frame numeric offsets are handled separately
 *  in `parseFrame`, which tokenizes the (paren-free) frame body directly. */
function scanDepth0Words(source: string, start: number, end: number, ctx: SkipContext): Word[] {
  const out: Word[] = [];
  let i = start;
  let depth = 0;
  while (i < end) {
    const skipped = trySkipNonCode(source, i, end, ctx);
    if (skipped >= 0) { i = skipped > i ? skipped : i + 1; continue; }
    const c = source[i];
    if (c === '(') { depth++; i++; continue; }
    if (c === ')') { if (depth > 0) depth--; i++; continue; }
    if (depth === 0 && IDENT_START.test(c)) {
      const s = i;
      i++;
      while (i < end && IDENT_CONT.test(source[i])) i++;
      out.push({ start: s, end: i, upper: source.slice(s, i).toUpperCase() });
      continue;
    }
    i++;
  }
  return out;
}

/** Parse a frame body (`BETWEEN <b1> AND <b2>` or `<b1>`) into a `FrameSpec`.
 *  Returns `null` for anything non-standard (e.g. an INTERVAL offset), letting
 *  the caller fall back to the raw textarea. */
function parseFrame(unitUpper: string, frameBody: string): FrameSpec | null {
  const unit = unitUpper.toLowerCase() as FrameUnit;
  const toks = frameBody.trim().split(/\s+/).filter(Boolean);
  if (toks.length === 0) return null;

  if (toks[0].toUpperCase() === 'BETWEEN') {
    const andIdx = toks.findIndex((t, k) => k > 0 && t.toUpperCase() === 'AND');
    if (andIdx < 0) return null;
    const start = parseBound(toks.slice(1, andIdx));
    const end = parseBound(toks.slice(andIdx + 1));
    if (!start || !end) return null;
    return { unit, start, end };
  }

  const start = parseBound(toks);
  if (!start) return null;
  return { unit, start, end: null };
}

function parseBound(toks: readonly string[]): FrameBound | null {
  const upper = toks.map((t) => t.toUpperCase());
  const joined = upper.join(' ');
  if (joined === 'UNBOUNDED PRECEDING') return { kind: 'unbounded-preceding' };
  if (joined === 'UNBOUNDED FOLLOWING') return { kind: 'unbounded-following' };
  if (joined === 'CURRENT ROW') return { kind: 'current-row' };
  if (toks.length === 2 && /^\d+$/.test(toks[0])) {
    if (upper[1] === 'PRECEDING') return { kind: 'n-preceding', n: Number(toks[0]) };
    if (upper[1] === 'FOLLOWING') return { kind: 'n-following', n: Number(toks[0]) };
  }
  return null;
}

/** Case- + whitespace-insensitive normalization for the round-trip self-check.
 *  serialize only re-cases keywords + normalizes separator whitespace, and
 *  preserves expression bytes verbatim, so stripping whitespace + lowercasing
 *  compares token identity exactly. */
function normalize(s: string): string {
  return s.replace(/\s+/g, '').toLowerCase();
}
